import { SearchParams } from './pagination'
import { InviteMember, TeamMemberStatus } from './members'
import { Role } from '~/types/users'
import { ApiVersion } from './version'
import { BusinessEntity } from './countries'

export type Partner = {
  id: string
  name: string
  phoneNumber?: string
  city?: string
  country?: string
  email?: string
  postalCode?: string
  salesForceCustomerId?: string
  chargebeeCustomerId?: string
  state?: string
  status?: PartnerStatus
  street?: string
  invoiceEmail?: string | null
  vatNumber?: string
  tier?: PartnerTier
  billingCity?: string
  billingCountry?: string
  billingName?: string
  billingPostalCode?: string
  billingState?: string
  billingStreet?: string
  billingVatNumber?: string
  netTermDayOptions?: PartnerNetTermDays
  contractType?: PartnerContractType
  renewalContactEmail?: string
  tierDiscountPercentage?: string
}

export interface PartnerWithBusinessEntity extends Partial<Partner> {
  businessEntity?: BusinessEntity
  address?: string
}

export type PartnersResponse = {
  data: Partner[]
  pages: number
  total: number
}

export enum PartnerNetTermDays {
  NET_15 = 'NET_15',
  NET_30 = 'NET_30',
  NET_45 = 'NET_45',
  NET_60 = 'NET_60',
  NET_90 = 'NET_90',
}

export enum PartnerTier {
  SOLUTIONS = 'SOLUTIONS',
  BUSINESS = 'BUSINESS',
  AUTHORIZED = 'AUTHORIZED',
  AFFILIATE = 'AFFILIATE',
  GLOBAL_ALLIANCE = 'GLOBAL_ALLIANCE',
  LATAM_SOLUTIONS = 'LATAM_SOLUTIONS',
  LATAM_BUSINESS = 'LATAM_BUSINESS',
  IGNITE = 'IGNITE',
}

export type UserInvitePayload = {
  invites: InviteMember[]
}

export enum PartnerStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
}

export enum InvoiceStatus {
  ALL = 'ALL',
  PAID = 'PAID',
  POSTED = 'POSTED',
  PAYMENT_DUE = 'PAYMENT_DUE',
  NOT_PAID = 'NOT_PAID',
  VOIDED = 'VOIDED',
  PENDING = 'PENDING',
}

export enum PartnerContractType {
  INDEPENDENT = 'INDEPENDENT',
  DISTRIBUTOR = 'DISTRIBUTOR',
  RESELLER = 'RESELLER',
}

export interface PartnersSearchParams extends SearchParams {
  country?: string
  status?: string
  name?: string
  contractType?: string
  distributorId?: string
}

export type PartnerUser = {
  id: string
  email: string
  firstName: string
  lastName: string
  role: Role
  status: TeamMemberStatus
  userId: string
}

export type PartnerRecord = Pick<
  PartnerUser,
  'firstName' | 'lastName' | 'email'
>

export type GetPartnerUsersResponse = {
  data: PartnerUser[]
  pages: number
  total: number
}

export type PaymentSource = {
  id: string
  type: string
  details: Record<string, string>
}

export type InvitePartnerResponse = {
  partnerId: string
}

export type InvitedUser = {
  userPartnerId: string
}

export type InviteUserResponse = InvitedUser[]

export type DemoAccount = {
  id: string
  inviteEmail: string
  accessLink: string
  productType: string
  apiVersion: ApiVersion
}

export type CreateDealerDemoAccountPayload = {
  inviteEmail: string
  apiVersion: ApiVersion
}

export type DownloadCsvSearchParams = SearchParams

export type PartnerTiersResponse = {
  partnerTier: PartnerTier
  discountPercentage: number
}

export enum PartnerOrderBy {
  CREATED_AT = 'CREATED_AT',
  NAME = 'NAME',
  STATUS = 'STATUS',
  COUNTRY = 'COUNTRY',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  ADDRESS = 'ADDRESS',
  STREET = 'STREET',
  CITY = 'CITY',
  POSTAL_CODE = 'POSTAL_CODE',
  EMAIL = 'EMAIL',
  SALESFORCE_CUSTOMER_ID = 'SALESFORCE_CUSTOMER_ID',
  DISTRIBUTOR_ID = 'DISTRIBUTOR_ID',
}

export enum PartnerUserOrderBy {
  CREATED_AT = 'CREATED_AT',
  STATUS = 'STATUS',
  EMAIL = 'EMAIL',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
}

export enum PartnerOnboardingStep {
  ACCOUNT_SETUP = 0,
  NOTIFICATION_SETTINGS = 1,
  TEAM_MEMBERS = 2,
}
